import React, { useCallback, useEffect, useState } from 'react';
import { Eye } from 'lucide-react';
import { resolveSignedDisplayUrl } from '@/utils/imageUrlUtils';
import TutorialImagePreviewModal from './TutorialImagePreviewModal';

export default function TutorialMarkdownImageBlock({
  meta,
  lineIndex,
  interactive = false,
  selected = false,
  onSelect,
  inRow = false,
}) {
  const [displaySrc, setDisplaySrc] = useState('');
  const [loadError, setLoadError] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const offsetX = meta.offsetX || 0;
  const offsetY = meta.offsetY || 0;

  useEffect(() => {
    let cancelled = false;
    setLoadError(false);
    (async () => {
      try {
        const resolved = await resolveSignedDisplayUrl(meta.src);
        if (!cancelled) setDisplaySrc(resolved || meta.src);
      } catch {
        if (!cancelled) {
          setDisplaySrc(meta.src);
          setLoadError(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [meta.src]);

  const figureClass = [
    'tutorial-markdown__figure',
    interactive ? 'tutorial-markdown__figure--interactive' : '',
    selected ? 'tutorial-markdown__figure--selected' : '',
  ].join('');

  const imgStyle = { maxWidth: '100%' };
  if (meta.width) imgStyle.width = `${meta.width}px`;
  if (meta.height) imgStyle.height = `${meta.height}px`;
  if (meta.width && !meta.height) imgStyle.height = 'auto';
  if (!meta.width && meta.height) imgStyle.width = 'auto';

  const figureStyle = {};
  if (offsetX || offsetY) {
    figureStyle.transform = `translate(${offsetX}px, ${offsetY}px)`;
  }

  const openPreview = useCallback((event) => {
    event?.stopPropagation();
    if (!displaySrc || loadError) return;
    setPreviewOpen(true);
  }, [displaySrc, loadError]);

  const align = meta.align || 'center';

  const figureInner = (
    <figure
      className={figureClass}
      style={Object.keys(figureStyle).length ? figureStyle : undefined}
      onClick={interactive ? (event) => {
        event.stopPropagation();
        onSelect?.(lineIndex);
      } : undefined}
    >
      {displaySrc ? (
        <div className="tutorial-markdown__figure-media">
          <img
            src={displaySrc}
            alt={meta.alt || ''}
            loading="eager"
            style={imgStyle}
            onError={() => setLoadError(true)}
            draggable={false}
          />
          {!loadError ? (
            <div className="tutorial-markdown__figure-hover" aria-hidden="true">
              <button
                type="button"
                className="tutorial-markdown__figure-preview-btn"
                aria-label="查看大图"
                onClick={openPreview}
                onMouseDown={(event) => event.stopPropagation()}
              >
                <Eye size={22} strokeWidth={2} />
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="tutorial-markdown__figure-placeholder">图片加载中…</div>
      )}
      {loadError ? <div className="tutorial-markdown__figure-error">图片无法加载，请检查地址或重新上传</div> : null}
    </figure>
  );

  return (
    <div className={`tutorial-markdown__figure-block${inRow ? ' tutorial-markdown__figure-block--in-row' : ''}`}>
      {inRow ? (
        figureInner
      ) : (
        <div className={`tutorial-markdown__figure-align tutorial-markdown__figure-align--${align}`}>
          {figureInner}
        </div>
      )}

      <TutorialImagePreviewModal
        open={previewOpen}
        src={displaySrc}
        alt={meta.alt || ''}
        onClose={() => setPreviewOpen(false)}
      />
    </div>
  );
}
